import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatCount, getCategoryStyle } from "@/utils/format";
import { categories } from "@/mocks/categories";

interface ArticleCardProps {
  article: Article;
  variant?: "stack" | "row" | "wide";
}

function categoryName(slug: string) {
  return categories.find((item) => item.slug === slug)?.name ?? "热瓜";
}

export default function ArticleCard({
  article,
  variant = "stack",
}: ArticleCardProps) {
  const style = getCategoryStyle(article.category);
  const catName = categoryName(article.category);

  if (variant === "row") {
    return (
      <article className="group flex gap-3 md:gap-4 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-200 transition-colors">
        <Link
          to={`/article/${article.id}`}
          className="w-24 h-24 sm:w-32 sm:h-24 md:w-40 md:h-28 shrink-0 rounded-md overflow-hidden block"
          aria-label={article.title}
        >
          <img
            src={article.cover}
            alt={`${article.title} - 瓜友会网${catName}爆料配图`}
            title={`${article.title} - 瓜友会网`}
            className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </Link>
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${style.chip}`}
            >
              <i className="ri-flashlight-line" aria-hidden="true"></i>
              {catName}
            </span>
            <span className="text-[11px] text-foreground-400">
              {article.publishTime}
            </span>
          </div>
          <h3 className="text-sm md:text-base font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            <Link to={`/article/${article.id}`}>{article.title}</Link>
          </h3>
          <p className="hidden sm:block mt-1.5 text-xs md:text-sm text-foreground-500 line-clamp-2 leading-relaxed">
            {article.summary}
          </p>
          <div className="mt-auto pt-2 flex items-center gap-3 text-[11px] md:text-xs text-foreground-400">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatCount(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {formatCount(article.comments)}
            </span>
            <span className="hidden md:inline-flex items-center gap-1">
              <i className="ri-user-line" aria-hidden="true"></i>
              {article.author}
            </span>
          </div>
        </div>
      </article>
    );
  }

  const isWide = variant === "wide";

  return (
    <article className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-colors">
      <Link
        to={`/article/${article.id}`}
        className={`block overflow-hidden ${isWide ? "h-52 md:h-64" : "h-40 md:h-44"}`}
        aria-label={article.title}
      >
        <img
          src={article.cover}
          alt={`${article.title} - 瓜友会网${catName}爆料配图`}
          title={`${article.title} - 瓜友会网`}
          className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2.5">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${style.chip}`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}></span>
            {catName}
          </span>
          <span className="text-[11px] text-foreground-400">
            {article.publishTime}
          </span>
        </div>
        <h3
          className={`font-medium text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors ${
            isWide ? "text-lg md:text-xl" : "text-base"
          }`}
        >
          <Link to={`/article/${article.id}`}>{article.title}</Link>
        </h3>
        <p className="mt-2 text-xs md:text-sm text-foreground-500 line-clamp-2 leading-relaxed">
          {article.summary}
        </p>
        <div className="mt-auto pt-4 flex items-center justify-between text-[11px] md:text-xs text-foreground-400">
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {formatCount(article.hot)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true"></i>
              {formatCount(article.comments)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            看现场
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </span>
        </div>
      </div>
    </article>
  );
}